import { useTranslation } from 'react-i18next';
import { Anchor, Box, Group, Stack, Text } from '@mantine/core';
import { IconTruck, IconUser } from '@tabler/icons-react';
import DocRow, { type DocDownloadControl } from '@/components/app/deals/steps/forwarding/DocRow';
import { fmtFwdDate } from '@/lib/forwarding';
import { formatMoney } from '@/lib/format';
import { telHref } from '@/lib/phone';
import type { BargainDocument } from '@/types/deals';

export interface VehicleView {
  readonly registrationNumber?: string;
  readonly trailerRegistrationNumber?: string;
  readonly ttnId?: string;
  /** кг; не передаётся для «Разобранного веса» (вес не фиксируется). */
  readonly weightKg?: number;
  readonly loadedDate?: string;
  readonly driverName?: string;
  readonly driverPhone?: string;
  readonly model?: string;
  readonly documents?: readonly BargainDocument[];
}

interface VehicleRowProps {
  readonly vehicle: VehicleView;
  /** Порядковый номер в списке (1..N). */
  readonly index?: number;
  /** Чем скачивать документы машины; по умолчанию — документы сделки. */
  readonly downloadControl?: DocDownloadControl;
}

// Карточка ТС: номер/прицеп + ТТН + вес (если есть) + дата отгрузки + водитель/модель + документы ТС.
// Универсальна для этапов Разобранный (без веса) / Отгружено / В пути / Доставлено.
export default function VehicleRow({ vehicle, index, downloadControl }: VehicleRowProps) {
  const { t } = useTranslation();
  const docs = vehicle.documents ?? [];
  const loaded = fmtFwdDate(vehicle.loadedDate);

  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      {index != null ? (
        <Text fz="xs" c="gray.6" fw={600} w={18} ta="right" style={{ flexShrink: 0 }}>
          {index}
        </Text>
      ) : null}
      <Box style={{ minWidth: 0, flex: 1 }}>
        <Group gap="xs" wrap="wrap">
          <IconTruck size={15} style={{ color: 'var(--mantine-color-gray-6)', flexShrink: 0 }} />
          <Text fz="sm" fw={500}>
            {vehicle.registrationNumber ?? '—'}
            {vehicle.trailerRegistrationNumber ? ` / ${vehicle.trailerRegistrationNumber}` : ''}
          </Text>
          {vehicle.weightKg != null ? (
            <Text fz="sm" c="gray.8">
              {formatMoney(vehicle.weightKg)} {t('app.deals.forwarding.kg')}
            </Text>
          ) : null}
          {vehicle.ttnId ? (
            <Text fz="xs" c="gray.6">
              {t('app.deals.forwarding.ttn')} {vehicle.ttnId}
            </Text>
          ) : null}
          {loaded ? (
            <Text fz="xs" c="gray.6">
              {t('app.deals.forwarding.shippedOn')} {loaded}
            </Text>
          ) : null}
        </Group>

        {vehicle.driverName || vehicle.model ? (
          <Group gap="xs" wrap="wrap" mt={2}>
            {vehicle.driverName ? (
              <Group gap={4} wrap="nowrap">
                <IconUser size={13} style={{ color: 'var(--mantine-color-gray-5)' }} />
                <Text fz="xs" c="gray.7">
                  {vehicle.driverName}
                </Text>
                {vehicle.driverPhone ? (
                  <Anchor href={telHref(vehicle.driverPhone)} fz="xs" c="brand.7">
                    {vehicle.driverPhone}
                  </Anchor>
                ) : null}
              </Group>
            ) : null}
            {vehicle.model ? (
              <Text fz="xs" c="gray.6">
                {vehicle.model}
              </Text>
            ) : null}
          </Group>
        ) : null}

        {docs.length > 0 ? (
          <Stack gap={4} mt={6}>
            {docs.map((doc, i) => (
              <DocRow key={doc.id ?? `${index}-${i}`} doc={doc} downloadControl={downloadControl} />
            ))}
          </Stack>
        ) : null}
      </Box>
    </Group>
  );
}
